import type { Component } from 'solid-js'
import { createResource, createSignal, Show, createMemo, createEffect } from 'solid-js'
import { useLocation } from '@solidjs/router'
import {
  getDateRangesForSimplePeriod,
  getDateRangesForCustomPeriod,
  formatDateToApiString,
  type SimplePeriodType,
  type CompareMode
} from '../../shared/helpers/date.functions'
import { useCorisEngagement } from '../../shared/providers/coris/coris-engagement.provider'
import { useUser } from '../../shared/providers/user-provider'
import { usePeriods } from '../../shared/providers/period.provider'
import {
  UsersMediaSortBy,
  UsersEventsSortBy,
  UsersForumSortBy,
  UsersChatSortBy
} from '../../features/engagement/domain/models/engagement-users'
import { AppRoutes } from '../../shared/app-routes'
import PeriodSelector from '../shared/components/PeriodSelector'
import TopEngagementCard from '../shared/components/TopEngagementCard'
import EngagementUsersTable from '../shared/components/EngagementUsersTable'
import EmptyState from '../shared/components/EmptyState'
import { sticky } from '../../shared/directives/sticky'
import { SkeletonEngagementCard } from '../shared/components/Skeleton'

// Prevent tree-shaking of directive
sticky

const PAGE_SIZE = 15

const EngagementUsers: Component = () => {
  const location = useLocation()
  const { engagementPeriod, setEngagementPeriod } = usePeriods()
  const [page, setPage] = createSignal(1)
  const [isStuck, setIsStuck] = createSignal(false)
  const [sortBy, setSortBy] = createSignal<string | null>(null)
  const [sortOrder, setSortOrder] = createSignal<'asc' | 'desc'>('desc')

  const category = createMemo<'media' | 'events' | 'forum' | 'chat'>(() => {
    if (location.pathname === AppRoutes.TopSongListeners()) return 'media'
    if (location.pathname === AppRoutes.TopForumPosters()) return 'forum'
    if (location.pathname === AppRoutes.TopChatMessengers()) return 'chat'
    // Add events route if it exists in AppRoutes, otherwise default to media or undefined
    return 'media'
  })

  const isSubRoute = createMemo(() => {
    return [AppRoutes.TopSongListeners(), AppRoutes.TopForumPosters(), AppRoutes.TopChatMessengers()].includes(
      location.pathname
    )
  })

  const { getUsersMedia, getUsersEvents, getUsersForum, getUsersChat, getUsersTotals } = useCorisEngagement()
  const { organizationSlug } = useUser()

  // Set default sorting based on category
  createEffect(() => {
    const currentCategory = category()
    if (currentCategory === 'media') {
      setSortBy(UsersMediaSortBy.SONGS_PLAYED)
    } else if (currentCategory === 'events') {
      setSortBy(UsersEventsSortBy.BUY_NOW_CLICKS)
    } else if (currentCategory === 'forum') {
      setSortBy(UsersForumSortBy.VIEWS)
    } else if (currentCategory === 'chat') {
      setSortBy(UsersChatSortBy.MESSAGES)
    }
    setSortOrder('desc')
  })

  const [summaryData] = createResource(
    () => {
      const organization = organizationSlug()
      if (!organization || isSubRoute()) return null
      return {
        ...engagementPeriod(),
        organization
      }
    },
    async (params) => {
      if (!params) return null

      const dateRanges =
        params.selectedPeriod === 'custom' && params.customDateRange
          ? getDateRangesForCustomPeriod(params.customDateRange.from, params.customDateRange.to)
          : getDateRangesForSimplePeriod(params.selectedPeriod)

      const commonParams = {
        fromDate: dateRanges.fromDate,
        toDate: dateRanges.toDate,
        organization: params.organization
      }

      const [media, forum, chat] = await Promise.all([
        getUsersTotals.execute({ ...commonParams, activityType: 'media' }),
        getUsersTotals.execute({ ...commonParams, activityType: 'forum' }),
        getUsersTotals.execute({ ...commonParams, activityType: 'chat' })
      ])

      return {
        topMedia: media.users,
        topForum: forum.users,
        topChat: chat.users
      }
    }
  )

  const [tableData] = createResource(
    () => {
      const organization = organizationSlug()
      if (!organization) return null
      return {
        ...engagementPeriod(),
        page: page(),
        organization,
        category: category(),
        sortBy: sortBy(),
        sortOrder: sortOrder()
      }
    },
    async (params) => {
      if (!params) return null

      const dateRanges =
        params.selectedPeriod === 'custom' && params.customDateRange
          ? getDateRangesForCustomPeriod(params.customDateRange.from, params.customDateRange.to)
          : getDateRangesForSimplePeriod(params.selectedPeriod)

      let fromPrevious = dateRanges.fromPrevious
      let toPrevious = dateRanges.toPrevious

      if (params.compareMode === 'custom_range' && params.compareCustomRange) {
        fromPrevious = formatDateToApiString(params.compareCustomRange.from)
        toPrevious = formatDateToApiString(params.compareCustomRange.to)
      }

      const commonParams = {
        fromDate: dateRanges.fromDate,
        toDate: dateRanges.toDate,
        fromPrevious,
        toPrevious,
        offset: (params.page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
        organization: params.organization,
        sortOrder: params.sortOrder
      }

      if (params.category === 'media') {
        return getUsersMedia.execute({
          ...commonParams,
          sortBy: (params.sortBy as UsersMediaSortBy) ?? UsersMediaSortBy.SONGS_PLAYED
        })
      }

      if (params.category === 'events') {
        return getUsersEvents.execute({
          ...commonParams,
          sortBy: (params.sortBy as UsersEventsSortBy) ?? UsersEventsSortBy.BUY_NOW_CLICKS
        })
      }

      if (params.category === 'forum') {
        return getUsersForum.execute({
          ...commonParams,
          sortBy: (params.sortBy as UsersForumSortBy) ?? UsersForumSortBy.VIEWS
        })
      }

      if (params.category === 'chat') {
        return getUsersChat.execute({
          ...commonParams,
          sortBy: (params.sortBy as UsersChatSortBy) ?? UsersChatSortBy.MESSAGES
        })
      }

      return null
    }
  )

  const handlePeriodChange = (period: SimplePeriodType) => {
    setEngagementPeriod({ selectedPeriod: period })
    setPage(1)
  }

  const handleCustomRangeChange = (from: Date, to: Date) => {
    setEngagementPeriod({ customDateRange: { from, to } })
    setPage(1)
  }

  const handleCompareModeChange = (mode: CompareMode) => {
    setEngagementPeriod({ compareMode: mode })
  }

  const handleCompareCustomRangeChange = (from: Date, to: Date) => {
    setEngagementPeriod({ compareCustomRange: { from, to } })
  }

  const handleSort = (column: string, direction: 'asc' | 'desc') => {
    setSortBy(column)
    setSortOrder(direction)
    setPage(1)
  }

  return (
    <div class="flex flex-col gap-4 px-6">
      <div
        use:sticky={setIsStuck}
        class="sticky top-0 z-10 py-6 transition-colors duration-200 px-6 -mx-6"
        classList={{
          'bg-white border-b border-gray-200': isStuck(),
          'bg-transparent border-b border-transparent': !isStuck()
        }}
      >
        <div class="mx-auto flex w-full max-w-[1280px] flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <PeriodSelector
            selectedPeriod={engagementPeriod().selectedPeriod}
            onPeriodChange={handlePeriodChange}
            customDateRange={engagementPeriod().customDateRange}
            onCustomRangeChange={handleCustomRangeChange}
            compareMode={engagementPeriod().compareMode}
            onCompareModeChange={handleCompareModeChange}
            compareCustomRange={engagementPeriod().compareCustomRange}
            onCompareCustomRangeChange={handleCompareCustomRangeChange}
          />
        </div>
      </div>

      <div class="mx-auto flex w-full max-w-[1280px] flex-col gap-8">
        <Show when={!tableData.loading && tableData() && tableData()!.items.length === 0}>
          <EmptyState />
        </Show>

        <Show when={tableData.loading || (tableData() && tableData()!.items.length > 0)}>
          <>
            <Show when={!isSubRoute()}>
              <Show
                when={summaryData()}
                fallback={
                  <div class="flex gap-6 overflow-x-auto pb-2 2xl:overflow-x-hidden 2xl:pb-0">
                    <SkeletonEngagementCard />
                    <SkeletonEngagementCard />
                    <SkeletonEngagementCard />
                  </div>
                }
              >
                <div class="flex gap-6 overflow-x-auto pb-2 2xl:overflow-x-hidden 2xl:pb-0">
                  <div class="flex-none min-w-[400px] 2xl:flex-1 2xl:min-w-0">
                    <TopEngagementCard
                      title="ls_top_song_listeners"
                      items={summaryData()!.topMedia}
                      viewAllUrl={AppRoutes.TopSongListeners()}
                    />
                  </div>
                  <div class="flex-none min-w-[400px] 2xl:flex-1 2xl:min-w-0">
                    <TopEngagementCard
                      title="ls_top_forum_posters"
                      items={summaryData()!.topForum}
                      viewAllUrl={AppRoutes.TopForumPosters()}
                    />
                  </div>
                  <div class="flex-none min-w-[400px] 2xl:flex-1 2xl:min-w-0">
                    <TopEngagementCard
                      title="ls_top_chat_messengers"
                      items={summaryData()!.topChat}
                      viewAllUrl={AppRoutes.TopChatMessengers()}
                    />
                  </div>
                </div>
              </Show>
            </Show>

            <EngagementUsersTable
              users={tableData()?.items ?? []}
              totalUsers={tableData()?.total ?? 0}
              page={page}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
              onSort={handleSort}
              category={category()}
              sortColumn={sortBy}
              sortDirection={sortOrder}
              isLoading={tableData.state === 'unresolved' || tableData.loading}
            />
          </>
        </Show>
      </div>
    </div>
  )
}

export default EngagementUsers
